import { useMemo, useState } from 'react';

import type { AssetInfo, ProductionState } from '@/hooks/useProduction';

export interface ShotMeta {
  roll: string;
  rollLabel: string;
  shotType: string;
  description: string;
  location: string;
  camera: string;
  scene: number;
}

const byName = (a: AssetInfo, b: AssetInfo) => a.shot_id.localeCompare(b.shot_id, undefined, { numeric: true });

const isRejected = (a: AssetInfo) => a.path.includes('/rejected/') || a.shot_id.includes('rejected/');

/**
 * Shared derivation behind the Storyboard and Dailies boards: split accepted from
 * rejected takes, read the shot plan for per-shot metadata, and produce the list of
 * tiles to show — every PLANNED shot (so not-yet-rendered ones appear as queued
 * placeholders and the board reflects the whole film) plus any stray rendered
 * assets that aren't in the plan.
 *
 * Both galleries previously carried their own near-identical copy of this.
 */
export function useShotBoard(assets: ProductionState['assets'], shotPlan: unknown, type: AssetInfo['type']) {
  // Tracks assets whose media 404'd — e.g. an attempt moved to rejected/ before the
  // accepted regeneration landed. Keyed by URL so a new cache-busted path retries.
  const [failedSrcs, setFailedSrcs] = useState<Set<string>>(new Set());
  const markFailed = (src: string) => setFailedSrcs((s) => (s.has(src) ? s : new Set(s).add(src)));

  const board = useMemo(() => {
    const all = assets.filter((a) => a.type === type).sort(byName);
    const items = all.filter((a) => !isRejected(a));
    const rejected = all.filter(isRejected);

    const metaById: Record<string, ShotMeta> = {};
    const plannedIds: string[] = [];

    if (shotPlan && typeof shotPlan === 'object' && 'shots' in (shotPlan as Record<string, unknown>)) {
      const shots = (
        shotPlan as {
          shots: {
            shot_id: string;
            roll?: string;
            shot_type?: string;
            description?: string;
            location_name?: string;
            camera?: string;
            camera_movement?: string;
            scene_number?: number;
          }[];
        }
      ).shots;

      for (const shot of shots) {
        const roll = shot.roll || 'a_roll';
        // Panel text, storyboard style: the shot description, with any trailing
        // "(camera: …)" note split out as the camera direction.
        let desc = String(shot.description || '').trim();
        let camera = String(shot.camera || shot.camera_movement || '').trim();
        const camMatch = desc.match(/\(camera:\s*([^)]+)\)\s*$/i);
        if (camMatch) {
          camera = camera || camMatch[1].trim();
          desc = desc.slice(0, camMatch.index).trim();
        }
        metaById[shot.shot_id] = {
          roll,
          rollLabel: roll === 'a_roll' ? 'A-Roll' : 'B-Roll',
          shotType: shot.shot_type || '',
          description: desc,
          location: String(shot.location_name || '').replace(/\s*\[LOC_[A-Za-z0-9_]+\]\s*$/, ''),
          camera,
          scene: Number(shot.scene_number || 0),
        };
        plannedIds.push(shot.shot_id);
      }
    }

    const byShot = new Map(items.map((i) => [i.shot_id, i]));

    let displayIds: string[];
    if (plannedIds.length) {
      const planned = new Set(plannedIds);
      const extras = items.filter((i) => !planned.has(i.shot_id)).map((i) => i.shot_id);
      displayIds = [...plannedIds, ...extras].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    } else {
      displayIds = items.map((i) => i.shot_id);
    }

    return { all, items, rejected, metaById, byShot, displayIds };
  }, [assets, shotPlan, type]);

  return { ...board, failedSrcs, markFailed };
}
